import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { ColorModeProvider } from './contexts/ColorModeContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { UserEventProvider } from './contexts/UserEventContext';
import { SessionBookmarkProvider } from './contexts/SessionBookmarkContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminRoute from './components/common/AdminRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { configureAmplify } from './config/amplifyConfig';

// Lazy load components
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Events = lazy(() => import('./pages/Events'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MyPage = lazy(() => import('./pages/MyPage'));
const MySchedulePage = lazy(() => import('./pages/MySchedulePage'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const TimetablePage = lazy(() => import('./pages/TimetablePage'));
const AdminDataFetchPanel = lazy(() => import('./components/admin/AdminDataFetchPanel'));
const AdminEventSettings = lazy(() => import('./components/admin/AdminEventSettings'));
const AdminSessionManagement = lazy(() => import('./pages/AdminSessionManagement'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));

// Configure Amplify
configureAmplify();

// Loading component for lazy loading
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

function App() {
  return (
    <ErrorBoundary>
      <ColorModeProvider>
        <AuthProvider>
          <UserProvider>
            <NotificationProvider>
              <Router>
                <UserEventProvider>
                  <SessionBookmarkProvider>
                    <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                    {/* 認証 */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* 公開: 法務ページ (静的ルートは /:eventId より優先してマッチ) */}
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<Terms />} />

                    {/* 公開: イベント一覧 */}
                    <Route path="/events" element={<Events />} />

                    {/* 公開: イベントタイムテーブル */}
                    <Route path="/:eventId" element={<TimetablePage />} />

                    {/* 管理者: 管理画面 */}
                    <Route
                      path="/admin/dashboard"
                      element={
                        <AdminRoute>
                          <Dashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <AdminRoute>
                          <UserManagement />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/events"
                      element={
                        <AdminRoute>
                          <AdminEventSettings />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/sessions"
                      element={
                        <AdminRoute>
                          <AdminSessionManagement />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/fetch"
                      element={
                        <AdminRoute>
                          <AdminDataFetchPanel />
                        </AdminRoute>
                      }
                    />

                    {/* マイページ（全ログインユーザー） */}
                    <Route
                      path="/mypage"
                      element={
                        <ProtectedRoute>
                          <MyPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* マイスケジュール（全ログインユーザー、Bookmark 済 session 表示） */}
                    <Route
                      path="/my-schedule"
                      element={
                        <ProtectedRoute>
                          <MySchedulePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* ルート: LP を表示 (公開、未ログイン/ログイン済両対応) */}
                    <Route path="/" element={<LandingPage />} />
                    {/* 未知ルート: LP に redirect (2 階層以上の未知パスのみマッチ、`/:eventId` は上で先にマッチする) */}
                    <Route path="*" element={<LandingPage />} />
                    </Routes>
                    </Suspense>
                  </SessionBookmarkProvider>
                </UserEventProvider>
              </Router>
            </NotificationProvider>
          </UserProvider>
        </AuthProvider>
      </ColorModeProvider>
    </ErrorBoundary>
  );
}

export default App;